import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import { 
  Add as AddIcon, 
  UploadFile as UploadFileIcon,
  Description as DescriptionIcon,
  AutoAwesome as AutoAwesomeIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getCommonRegions, getFunds, getDocuments, getIntegrations, dealsApi, getDealById, getDealDocuments, getIntegrationFiles } from '../services/api';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Integration, IntegrationCategory, IntegrationStatus } from '../types/integrations';

const steps = ['Deal Details', 'Financials', 'Documents'];

const industries = [
  'Technology', 'Healthcare', 'Financial Services', 'Real Estate', 'Manufacturing',
  'Retail', 'Energy', 'Telecommunications', 'Media & Entertainment', 'Transportation',
  'Education', 'Food & Beverage', 'Other'
];

const sources = [
  'Investment Bank', 'Direct Outreach', 'Broker', 'Referral', 'Proprietary',
  'Auction Process', 'Other'
];

const dealTypes = [
  'Acquisition', 'Investment', 'Merger', 'Divestiture', 'Joint Venture', 'Other'
];

const currencies = ['USD', 'EUR', 'GBP', 'CAD'];

interface CreateDealDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (dealData: any) => Promise<any>;
}

interface DealData {
  name: string;
  company: string;
  industry: string;
  source: string;
  description: string;
  deal_type: string;
  region: string;
  tags: string[];
  estimatedValue: number ;
  currency: string;
  expectedCloseDate: string;
  uploadedFiles: {
    id: string;
    name: string;
    size: number;
    type: string;
    file: File;
  }[];
}

const CreateDealDialog: React.FC<CreateDealDialogProps> = ({ open, onClose, onSubmit }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [regions, setRegions] = useState<string[]>([]);
  const [createdDealId, setCreatedDealId] = useState<string | null>(null);
  const [funds, setFunds] = useState<any[]>([]);
  const [selectedFund, setSelectedFund] = useState<string>('auto');
  const [documentTabValue, setDocumentTabValue] = useState(0);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [selectedExistingDocuments, setSelectedExistingDocuments] = useState<Set<string>>(new Set());
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [noIntegrationsConnected, setNoIntegrationsConnected] = useState(false);
  const [integrationFiles, setIntegrationFiles] = useState<any[]>([]);
  const [integrationFilesLoading, setIntegrationFilesLoading] = useState(false);
  const [isSubfolderLoading, setIsSubfolderLoading] = useState(false);
  const [selectedIntegrationFiles, setSelectedIntegrationFiles] = useState<Set<string>>(new Set());
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [folderPath, setFolderPath] = useState<{ id: string; name: string }[]>([{ id: 'root', name: 'My Drive' }]);
  const [passwordPrompt, setPasswordPrompt] = useState<{ file: File; fileName: string } | null>(null);
  const [password, setPassword] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);

  const processingMessages = [
    'AI is analyzing your documents...',
    'Extracting financial data and metrics...',
    'Identifying key insights and trends...',
    'Generating deal analysis...',
  ];

  const handlePasswordSubmit = async () => {
    if (!passwordPrompt || !createdDealId) return;
    setIsCheckingPassword(true);
    
    try {
      const formData = new FormData();
      formData.append('file', passwordPrompt.file);
      formData.append('document_type', 'CIM');
      formData.append('password', password);
      
      // const response = await api.post(`/api/v1/deals/${createdDealId}/documents/upload`, formData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // });
      
      const fileName = passwordPrompt.fileName;
      setPasswordPrompt(null);
      setPassword('');
      
      // Continue with document processing, passing the uploaded file name to skip it
      await processDocuments(createdDealId, [fileName]);
      
    } catch (error: any) {
      console.error('Password upload failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || '';
      
      if (errorMessage.startsWith('incorrect_password:')) {
        window.dispatchEvent(new CustomEvent('apiError', { detail: 'Incorrect password. Please try again.' }));
      } else {
        setPasswordPrompt(null);
        setPassword('');
        setProcessingStatus({
          stage: 'error',
          message: `Failed to upload with password: ${errorMessage}`,
          progress: 0,
          documentsProcessed: 0,
          totalDocuments: dealData.uploadedFiles.length,
          canClose: true,
        });
      }
    } finally {
      setIsCheckingPassword(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordPrompt(null);
    setPassword('');
  };

  const [dealData, setDealData] = useState<DealData>({
    name: '',
    company: '',
    industry: '',
    source: '',
    description: '',
    deal_type: '',
    region: '',
    tags: [],
    estimatedValue: 0,
    currency: 'USD',
    expectedCloseDate: '',
    uploadedFiles: [],
  });
  const [dateError, setDateError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<{
    stage: 'idle' | 'creating' | 'uploading' | 'processing' | 'scoring' | 'completed' | 'error';
    message: string;
    progress: number;
    documentsProcessed: number;
    totalDocuments: number;
    canClose: boolean;
  }>({
    stage: 'idle',
    message: '',
    progress: 0,
    documentsProcessed: 0,
    totalDocuments: 0,
    canClose: true,
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const regionsData = await getCommonRegions();
        setRegions(regionsData);
        setIntegrationsLoading(true);
        const integrationsResponse = await getIntegrations();

        if (integrationsResponse && integrationsResponse.length === 0) {
          setNoIntegrationsConnected(true);
          setIntegrations([]);
        } else {
          setNoIntegrationsConnected(false);
          const integrations = integrationsResponse.map((apiIntegration: { id: string; tool_name: string }) => {
            const displayName = apiIntegration.tool_name.charAt(0).toUpperCase() + apiIntegration.tool_name.slice(1);
            
            return {
              id: apiIntegration.id,
              name: displayName,
              description: `${displayName} integration`,
              category: IntegrationCategory.DocumentRepository,
              status: IntegrationStatus.Connected,
              isConnected: true,
            };
          });
          setIntegrations(integrations);
        }
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
        setNoIntegrationsConnected(true); // Assume no integrations if API call fails
        setIntegrations([]);
      } finally {
        setIntegrationsLoading(false);
      }
    };

    if (open) {
      fetchInitialData();
    }
  }, [open]);

  useEffect(() => {
    if (selectedIntegration) {
      fetchIntegrationFiles(selectedIntegration, currentFolderId);
    }
  }, [selectedIntegration, currentFolderId]);

  // Rotate processing messages every 3 seconds
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (processingStatus.stage === 'processing') {
      interval = setInterval(() => {
        setProcessingMessageIndex((prev) => (prev + 1) % processingMessages.length);
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [processingStatus.stage, processingMessages.length]);


  const resetState = () => {
    setActiveStep(0);
    setCreatedDealId(null);
    setFunds([]);
    setSelectedFund('auto');
    setDocumentTabValue(0);
    setExistingDocuments([]);
    setSelectedExistingDocuments(new Set());
    setDocumentsLoading(false);
    setSelectedIntegration(null);
    setIntegrationFiles([]);
    setIntegrationFilesLoading(false);
    setIsSubfolderLoading(false);
    setSelectedIntegrationFiles(new Set());
    setCurrentFolderId('root');
    setFolderPath([{ id: 'root', name: 'My Drive' }]);
    setDealData({
      name: '',
      company: '',
      industry: '',
      source: '',
      description: '',
      deal_type: '',
      region: '',
      tags: [],
      estimatedValue: 0,
      currency: 'USD',
      expectedCloseDate: '',
      uploadedFiles: [],
    });
    setDateError('');
    setIsSubmitting(false);
    setProcessingStatus({
      stage: 'idle',
      message: '',
      progress: 0,
      documentsProcessed: 0,
      totalDocuments: 0,
      canClose: true,
    });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleNext = () => {
    // Validate required fields for step 0 (Deal Details)
    if (activeStep === 0) {
      const errors: {[key: string]: string} = {};
      
      if (!dealData.name.trim()) {
        errors.name = 'Deal name is required';
      }
      
      if (!dealData.deal_type) {
        errors.deal_type = 'Deal type is required';
      }
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }
      
      // Clear errors if validation passes
      setValidationErrors({});
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleDocumentTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDocumentTabValue(newValue);
    setDealData(prevData => ({
      ...prevData,
      uploadedFiles: [],
    }));
    setSelectedExistingDocuments(new Set());
    setSelectedIntegrationFiles(new Set());
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === 'expectedCloseDate') {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() + 100);

      if (value && selectedDate < today) {
        setDateError('Close date cannot be in the past.');
      } else if (value && selectedDate > maxDate) {
        setDateError('Close date cannot be more than 100 years in the future.');
      } else {
        setDateError('');
      }
    }

    setDealData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const files = filesArray.map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file,
      }));
      setDealData((prevData) => ({
        ...prevData,
        uploadedFiles: [...prevData.uploadedFiles, ...files],
      }));
      
      // Fetch funds when documents are uploaded
      if (funds.length === 0) {
        try {
          const fundsData = await getFunds();
          setFunds(fundsData.funds || []);
        } catch (error) {
          console.error('Failed to fetch funds:', error);
        }
      }
    }
  };

  const handleIntegrationFileSelect = (file: any, isSelected: boolean) => {
    if (isSelected) {
      const newFile = {
        id: file.id,
        name: file.name,
        size: file.size || 0,
        type: file.mimeType || 'application/octet-stream',
        file: null,
        source: selectedIntegration?.name,
      };
      // @ts-ignore
      setDealData(prevData => ({
        ...prevData,
        uploadedFiles: [...prevData.uploadedFiles, newFile],
      }));
    } else {
      setDealData(prevData => ({
        ...prevData,
        uploadedFiles: prevData.uploadedFiles.filter(f => f.id !== file.id),
      }));
    }
  };


  const handleSubmit = async () => {
    setIsSubmitting(true);
    const totalDocuments = dealData.uploadedFiles.length + selectedExistingDocuments.size;
    
    setProcessingStatus({
      stage: 'creating',
      message: 'Creating deal...',
      progress: 10,
      documentsProcessed: 0,
      totalDocuments,
      canClose: false,
    });

    try {
      const submissionData = {
        ...dealData,
        deal_type: dealData.deal_type.toLowerCase().replace(/ /g, '_'),
        expectedCloseDate: dealData.expectedCloseDate || new Date().toISOString().split('T')[0],
        selectedExistingDocuments: Array.from(selectedExistingDocuments), // Include selected existing documents
      };
      
      // Create the deal first
      const createdDeal = await onSubmit(submissionData);
      setCreatedDealId(createdDeal?.id || null);
      
      setProcessingStatus(prev => ({
        ...prev,
        stage: 'uploading',
        message: 'Deal created successfully! Processing documents...',
        progress: 30,
      }));

      // Process documents (both uploaded and existing)
      if ((dealData.uploadedFiles.length > 0 || selectedExistingDocuments.size > 0) && createdDeal?.id) {
        await processDocuments(createdDeal.id);
      } else {
        // No documents to process, complete immediately
        setProcessingStatus({
          stage: 'completed',
          message: 'Deal created successfully!',
          progress: 100,
          documentsProcessed: 0,
          totalDocuments: 0,
          canClose: true,
        });
      }
      
    } catch (error) {
      console.error('Failed to create deal:', error);
      setProcessingStatus({
        stage: 'error',
        message: 'Failed to create deal. Please try again.',
        progress: 0,
        documentsProcessed: 0,
        totalDocuments: dealData.uploadedFiles.length,
        canClose: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const processDocuments = async (dealId: string, uploadedFileNames: string[] = []) => {
    try {
      let processedCount = 0;
      const documentIds: string[] = [];
      const totalDocuments = dealData.uploadedFiles.length + selectedExistingDocuments.size;

      // First, handle uploaded documents (skip already uploaded ones)
      for (const fileData of dealData.uploadedFiles) {
        // Skip if this file was already uploaded with password
        if (uploadedFileNames.includes(fileData.name)) {
          processedCount++;
          continue;
        }
        const currentProcessedCount = processedCount;
        setProcessingStatus(prev => ({
          ...prev,
          stage: 'uploading',
          message: `Uploading ${fileData.name}...`,
          progress: 30 + (currentProcessedCount / totalDocuments) * 30,
          documentsProcessed: currentProcessedCount,
        }));

        try {
          let result;
          // @ts-ignore
          if (fileData.source) {
            // @ts-ignore
            const toolName = fileData.source.toLowerCase().replace(/\s/g, '');
            result = await dealsApi.uploadDocumentFromIntegration(dealId, toolName, [fileData.id], fileData.type);
            console.log(`Document ${fileData.name} from integration uploaded:`, result);
          } else {
            // Get the actual file and password from fileData
            const actualFile = fileData.file;
            // @ts-ignore
            const filePassword = fileData.password || '';
            
            const formData = new FormData();
            formData.append('file', actualFile);
            formData.append('document_type', 'CIM');
            if (filePassword) {
              formData.append('password', filePassword);
            }
            
            try {
              const response = await api.post(`/api/v1/deals/${dealId}/documents/upload`, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });
              result = response.data;
              console.log(`Document ${fileData.name} uploaded:`, result);
            } catch (uploadError: any) {
              // Check if error is due to password protection
              const errorMessage = uploadError.response?.data?.detail || uploadError.response?.data?.message || uploadError.message || '';
              
              if (errorMessage.startsWith('password_required:')) {
                // Show password prompt for this file
                setPasswordPrompt({ file: actualFile, fileName: fileData.name });
                setProcessingStatus({
                  stage: 'uploading',
                  message: 'Password required for document',
                  progress: 30,
                  documentsProcessed: processedCount,
                  totalDocuments,
                  canClose: false,
                });
                return; // Stop processing and wait for password
              }
              
              // Re-throw other errors
              throw uploadError;
            }
          }
          documentIds.push(result.document_id);
          processedCount++;
        } catch (docError) {
          console.error(`Error uploading document ${fileData.name}:`, docError);
          setProcessingStatus({
            stage: 'error',
            message: `Failed to upload ${fileData.name}: ${docError instanceof Error ? docError.message : 'Unknown error'}`,
            progress: 0,
            documentsProcessed: processedCount,
            totalDocuments,
            canClose: true,
          });
          return;
        }
      }

      // Second, handle existing documents
      if (selectedExistingDocuments.size > 0) {
        setProcessingStatus(prev => ({
          ...prev,
          stage: 'uploading',
          message: 'Linking existing documents...',
          progress: 30 + (processedCount / totalDocuments) * 30,
          documentsProcessed: processedCount,
        }));

        try {
          const result = await dealsApi.linkDocumentsToDeal(dealId, Array.from(selectedExistingDocuments));
          console.log('Existing documents linked:', result);
          documentIds.push(...Array.from(selectedExistingDocuments));
          processedCount += selectedExistingDocuments.size;
        } catch (docError) {
          console.error('Error linking existing documents:', docError);
          setProcessingStatus({
            stage: 'error',
            message: `Failed to link existing documents: ${docError instanceof Error ? docError.message : 'Unknown error'}`,
            progress: 0,
            documentsProcessed: processedCount,
            totalDocuments,
            canClose: true,
          });
          return;
        }
      }

      // Now poll for processing completion
      setProcessingStatus(prev => ({
        ...prev,
        stage: 'processing',
        message: 'AI processing documents...',
        progress: 60,
        documentsProcessed: processedCount,
      }));

      // Poll for document processing status
      await pollDocumentProcessingStatus(dealId, documentIds);

    } catch (error) {
      console.error('Error processing documents:', error);
      setProcessingStatus({
        stage: 'error',
        message: `Processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        progress: 0,
        documentsProcessed: 0,
        totalDocuments: dealData.uploadedFiles.length + selectedExistingDocuments.size,
        canClose: true,
      });
    }
  };

  const pollDocumentProcessingStatus = async (dealId: string, documentIds: string[]) => {
    const maxAttempts = 60; // 5 minutes max (5 second intervals)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const documents = await getDealDocuments(dealId);
        const relevantDocs = documents.length > 0 ? documents : [];
        const completedDocs = relevantDocs.filter((doc: any) =>
          doc.processing_status === 'completed'
        );
        const failedDocs = relevantDocs.filter((doc: any) =>
          doc.processing_status === 'failed'
        );

        console.log('Polling status:', {
          documentIds,
          relevantDocs: relevantDocs.map((d: any) => ({ id: d.id, status: d.processing_status })),
          completedDocs: completedDocs.length,
          failedDocs: failedDocs.length
        });

        if (failedDocs.length > 0) {
          setProcessingStatus({
            stage: 'error',
            message: `Document processing failed for ${failedDocs.length} document(s)`,
            progress: 0,
            documentsProcessed: completedDocs.length,
            totalDocuments: documentIds.length,
            canClose: true,
          });
          return;
        }

        const expectedDocuments = dealData.uploadedFiles.length + selectedExistingDocuments.size;

        if (completedDocs.length >= expectedDocuments) {
          setProcessingStatus({
            stage: 'completed',
            message: 'Documents processed successfully! Starting scoring...',
            progress: 80,
            documentsProcessed: completedDocs.length,
            totalDocuments: expectedDocuments,
            canClose: true,
          });
          await startScoring(dealId, selectedFund);
          return;
        }

        const progress = 60 + (completedDocs.length / expectedDocuments) * 20;
        setProcessingStatus(prev => ({
          ...prev,
          progress,
          documentsProcessed: completedDocs.length,
          message: `Processing documents... ${completedDocs.length}/${expectedDocuments} complete`,
        }));

        attempts++;
        await new Promise(resolve => setTimeout(resolve, 5000));

      } catch (error) {
        console.error('Error polling document status:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }

    setProcessingStatus({
      stage: 'error',
      message: 'Document processing timed out. Processing may continue in background.',
      progress: 0,
      documentsProcessed: 0,
      totalDocuments: documentIds.length,
      canClose: true,
    });
  };

  const startScoring = async (dealId: string, fundId: string) => {
    setProcessingStatus(prev => ({
      ...prev,
      stage: 'scoring',
      message: `Scoring deal against ${fundId === 'auto' ? 'best matching fund' : 'selected fund'}...`,
      progress: 85,
    }));

    try {
      await dealsApi.scoreDeal(dealId, fundId);
      await pollScoringStatus(dealId);
    } catch (error) {
      console.error('Error starting scoring:', error);
      setProcessingStatus({
        stage: 'error',
        message: `Failed to start scoring: ${error instanceof Error ? error.message : 'Unknown error'}`,
        progress: 0,
        documentsProcessed: dealData.uploadedFiles.length,
        totalDocuments: dealData.uploadedFiles.length,
        canClose: true,
      });
    }
  };

  const pollScoringStatus = async (dealId: string) => {
    const maxAttempts = 24; // 2 minutes max (5 second intervals)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const deal = await getDealById(dealId);

        if (deal.scoring_completed === 'completed') {
          let scoreMessage = 'Processing complete! Deal scoring finished successfully.';
          if (deal.score) {
            scoreMessage = `Processing complete! Deal scored: ${deal.score.overall_score}/10 (${deal.score.recommendation})`;
          }
          setProcessingStatus({
            stage: 'completed',
            message: scoreMessage,
            progress: 100,
            documentsProcessed: dealData.uploadedFiles.length,
            totalDocuments: dealData.uploadedFiles.length,
            canClose: true,
          });
          return;
        }

        if (deal.scoring_completed === 'failed') {
          setProcessingStatus({
            stage: 'error',
            message: 'Deal scoring failed. Please try again or contact support.',
            progress: 0,
            documentsProcessed: dealData.uploadedFiles.length,
            totalDocuments: dealData.uploadedFiles.length,
            canClose: true,
          });
          return;
        }

        attempts++;
        await new Promise(resolve => setTimeout(resolve, 5000));

      } catch (error) {
        console.error('Error polling scoring status:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }

    setProcessingStatus({
      stage: 'completed',
      message: 'Documents processed successfully! Scoring may continue in background.',
      progress: 100,
      documentsProcessed: dealData.uploadedFiles.length,
      totalDocuments: dealData.uploadedFiles.length,
      canClose: true,
    });
  };

  const fetchIntegrationFiles = async (integration: Integration, folderId: string = 'root') => {
    if (!integration.isConnected) {
      return;
    }
    if (folderId === 'root') {
      setIntegrationFilesLoading(true);
    } else {
      setIsSubfolderLoading(true);
    }
    setIntegrationFiles([]);
    setSelectedIntegrationFiles(new Set());
    try {
      const integrationName = integration.name.toLowerCase().replace(/\s/g, '');
      const response = await getIntegrationFiles(integrationName, folderId);
      
      if (response.status === 200) {
        const data = response.data;
        let files: any[] = [];
        if (integrationName === 'googledrive') {
          files = (data.files || []).map((file: any) => ({
            id: file.id,
            name: file.name,
            modified_time: file.modifiedTime,
            size: file.size ? parseInt(file.size, 10) : 0,
            mimeType: file.mimeType,
            isFolder: file.mimeType === 'application/vnd.google-apps.folder',
          }));
        } else if (integrationName === 'dropbox') {
          files = (data.entries || []).map((entry: any) => ({
            id: entry.path_display,
            name: entry.name,
            modified_time: entry.server_modified,
            size: entry.size,
            isFolder: entry['.tag'] === 'folder',
          }));
        } else if (integrationName === 'box') {
          files = (data.entries || []).map((entry: any) => ({
            id: entry.id,
            name: entry.name,
            modified_time: entry.modified_at,
            size: entry.size,
            isFolder: entry.type === 'folder',
          }));
     } else if (integrationName === 'onedrive') {
         files = (data.value || []).map((entry: any) => ({
           id: entry.id,
           name: entry.name,
           modified_time: entry.lastModifiedDateTime,
           size: entry.size,
           isFolder: !!entry.folder,
         }));
       }
    setIntegrationFiles(files);
  } else {
    console.error(`Failed to fetch files for ${integration.name}`);
        setIntegrationFiles([]);
      }
    } catch (error) {
      console.error(`Error fetching files for ${integration.name}:`, error);
      setIntegrationFiles([]);
    } finally {
      setIntegrationFilesLoading(false);
      setIsSubfolderLoading(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Deal Name *"
              type="text"
              fullWidth
              variant="outlined"
              value={dealData.name}
              onChange={handleChange}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              required
            />
            <TextField
              margin="dense"
              name="company"
              label="Company"
              type="text"
              fullWidth
              variant="outlined"
              value={dealData.company}
              onChange={handleChange}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Industry</InputLabel>
              <Select
                name="industry"
                value={dealData.industry}
                onChange={handleChange}
                label="Industry"
              >
                {industries.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel>Source</InputLabel>
              <Select
                name="source"
                value={dealData.source}
                onChange={handleChange}
                label="Source"
              >
                {sources.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="description"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={dealData.description}
              onChange={handleChange}
            />
            <FormControl fullWidth margin="dense" error={!!validationErrors.deal_type} required>
              <InputLabel>Deal Type *</InputLabel>
              <Select
                name="deal_type"
                value={dealData.deal_type}
                onChange={handleChange}
                label="Deal Type *"
              >
                {dealTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel>Region</InputLabel>
              <Select
                name="region"
                value={dealData.region}
                onChange={handleChange}
                label="Region"
              >
                {regions.map((region) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        );
      case 1:
        return (
          <>
            <TextField
              margin="dense"
              name="estimatedValue"
              label="Estimated Value"
              type="number"
              fullWidth
              variant="outlined"
              value={dealData.estimatedValue}
              onChange={handleChange}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Currency</InputLabel>
              <Select
                name="currency"
                value={dealData.currency}
                onChange={handleChange}
                label="Currency"
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="expectedCloseDate"
              label="Expected Close Date"
              type="date"
              fullWidth
              variant="outlined"
              value={dealData.expectedCloseDate}
              onChange={handleChange}
              error={!!dateError}
              helperText={dateError}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </>
        );
      case 2:
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>AI-Powered Document Processing:</strong> Upload new documents or select existing ones to automatically extract key data and generate deal scores.
              </Typography>
            </Alert>
            
            {/* Document Selection Tabs */}
            {processingStatus.stage === 'idle' && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={documentTabValue} onChange={handleDocumentTabChange}>
                  <Tab label="Upload New" icon={<UploadFileIcon />} iconPosition="start" />
                  <Tab label="Select Existing" icon={<FolderIcon />} iconPosition="start" />
                  <Tab label="Integrations" icon={<CloudUploadIcon />} iconPosition="start" />
                </Tabs>
              </Box>
            )}
            
            {processingStatus.stage === 'idle' && (
              <Box mt={2}>
                {documentTabValue === 0 && (
                  // Upload New Documents Tab
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadFileIcon />}
                      fullWidth
                      sx={{ mb: 2, py: 1.5 }}
                    >
                      Upload Documents (PDF, DOC, DOCX)
                      <input
                        type="file"
                        hidden
                        multiple
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileUpload}
                      />
                    </Button>
                  </>
                )}
                {documentTabValue === 1 && (
                  // Select Existing Documents Tab
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<FolderIcon />}
                      fullWidth
                      sx={{ mb: 2, py: 1.5 }}
                      onClick={async () => {
                        setDocumentsLoading(true);
                        try {
                          const response = await getDocuments({size: 1000});
                          const docs = Array.isArray(response) ? response : response.documents || [];
                          // Filter to only show documents with completed embeddings
                          const readyDocs = docs.filter((doc: any) => doc.embedding_status === 'completed');
                          setExistingDocuments(readyDocs);
                          
                          // Fetch funds when documents are loaded (similar to file upload)
                          if (funds.length === 0) {
                            try {
                              const fundsData = await getFunds();
                              setFunds(fundsData.funds || []);
                            } catch (error) {
                              console.error('Failed to fetch funds:', error);
                            }
                          }
                        } catch (error) {
                          console.error('Failed to fetch documents:', error);
                        } finally {
                          setDocumentsLoading(false);
                        }
                      }}
                    >
                      {documentsLoading ? <CircularProgress size={20} /> : 'Load Ready Documents'}
                    </Button>
                  
                    
                    {existingDocuments.length > 0 && (
                      <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    indeterminate={selectedExistingDocuments.size > 0 && selectedExistingDocuments.size < existingDocuments.length}
                                    checked={existingDocuments.length > 0 && selectedExistingDocuments.size === existingDocuments.length}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedExistingDocuments(new Set(existingDocuments.map(doc => doc.id)));
                                      } else {
                                        setSelectedExistingDocuments(new Set());
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>Document</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Tags</TableCell>
                                <TableCell>Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {existingDocuments.map((doc) => (
                                <TableRow key={doc.id} hover>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selectedExistingDocuments.has(doc.id)}
                                      onChange={(e) => {
                                        const newSelected = new Set(selectedExistingDocuments);
                                        if (e.target.checked) {
                                          newSelected.add(doc.id);
                                        } else {
                                          newSelected.delete(doc.id);
                                        }
                                        setSelectedExistingDocuments(newSelected);
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <DescriptionIcon fontSize="small" color="primary" />
                                      <Typography variant="body2" noWrap>
                                        {doc.name}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {doc.type}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {doc.tags.map((tag: string) => (
                                        <Chip key={tag} label={tag} size="small" />
                                      ))}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {new Date(doc.uploaded_at).toLocaleDateString()}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )}
                    
                    {selectedExistingDocuments.size > 0 && processingStatus.stage === 'idle' && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>{selectedExistingDocuments.size} document(s) selected</strong> - These will be linked to the deal and processed for AI analysis.
                        </Typography>
                      </Alert>
                    )}
                  </>
                )}
                {documentTabValue === 2 && (
                  // Integrations Tab
                  <Box>
                    {selectedIntegration ? (
                      <>
                        {integrationFilesLoading ? (
                          <Box sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {folderPath.map((folder, index) => (
                                        <React.Fragment key={folder.id}>
                                            {index < folderPath.length - 1 ? (
                                                <Button
                                                    onClick={() => {
                                                        const newPath = folderPath.slice(0, index + 1);
                                                        setFolderPath(newPath);
                                                        setCurrentFolderId(folder.id);
                                                        if (selectedIntegration) {
                                                            fetchIntegrationFiles(selectedIntegration, folder.id);
                                                        }
                                                    }}
                                                >
                                                    {folder.name}
                                                </Button>
                                            ) : (
                                                <Typography variant="h6" sx={{ color: 'text.primary' }}>{folder.name}</Typography>
                                            )}
                                            {index < folderPath.length - 1 && <ChevronRightIcon />}
                                        </React.Fragment>
                                    ))}
                                </Box>
                                <Box>
                                    <Tooltip title="Refresh files">
                                        <IconButton onClick={() => selectedIntegration && fetchIntegrationFiles(selectedIntegration, currentFolderId)}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Button variant="outlined" startIcon={<ChevronLeftIcon />} onClick={() => {
                                      setDealData(prev => ({
                                          ...prev,
                                          uploadedFiles: prev.uploadedFiles.filter(f => (f as any).source !== selectedIntegration?.name)
                                      }));
                                      setSelectedIntegration(null);
                                    }} sx={{ ml: 1 }}>Back to Integrations</Button>
                                </Box>
                            </Box>
                            <Paper sx={{ maxHeight: 250, overflow: 'auto' }}>
                              <TableContainer>
                                <Table stickyHeader size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell padding="checkbox">
                                        <Checkbox
                                          indeterminate={selectedIntegrationFiles.size > 0 && selectedIntegrationFiles.size < integrationFiles.filter(f => !f.isFolder).length}
                                          checked={integrationFiles.filter(f => !f.isFolder).length > 0 && selectedIntegrationFiles.size === integrationFiles.filter(f => !f.isFolder).length}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setSelectedIntegrationFiles(new Set(integrationFiles.filter(f => !f.isFolder).map(file => file.id)));
                                            } else {
                                              setSelectedIntegrationFiles(new Set());
                                            }
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Modified</TableCell>
                                      <TableCell>Size</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {isSubfolderLoading ? (
                                      <TableRow>
                                        <TableCell colSpan={4} align="center">
                                          <CircularProgress />
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      integrationFiles.map((file) => (
                                        <TableRow
                                          key={file.id}
                                          hover
                                          sx={{
                                            cursor: file.isFolder ? 'pointer' : 'default',
                                            '&:hover': file.isFolder ? {
                                              bgcolor: 'action.hover',
                                            } : {}
                                          }}
                                          onClick={() => {
                                            if (file.isFolder) {
                                              setCurrentFolderId(file.id);
                                              setFolderPath([...folderPath, { id: file.id, name: file.name }]);
                                              if (selectedIntegration) {
                                                fetchIntegrationFiles(selectedIntegration, file.id);
                                              }
                                            }
                                          }}
                                        >
                                          <TableCell padding="checkbox">
                                            {!file.isFolder && (
                                              <Checkbox
                                                checked={dealData.uploadedFiles.some(f => f.id === file.id)}
                                                onChange={(e) => {
                                                  handleIntegrationFileSelect(file, e.target.checked);
                                                }}
                                              />
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {file.isFolder ? (
                                                <>
                                                  <FolderIcon fontSize="small" color="primary" />
                                                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                                    {file.name}
                                                  </Typography>
                                                  <ChevronRightIcon fontSize="small" color="action" />
                                                </>
                                              ) : (
                                                <>
                                                  <DescriptionIcon fontSize="small" color="action" />
                                                  <Typography variant="body2" noWrap>
                                                    {file.name}
                                                  </Typography>
                                                </>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="caption">
                                              {file.modified_time ? new Date(file.modified_time).toLocaleDateString() : '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="caption">
                                              {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : '-'}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Paper>
                          </>
                        )}
                      </>
                    ) : noIntegrationsConnected ? (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No Integrations Connected
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          It looks like you haven't connected any cloud storage integrations yet.
                          Connect your accounts to seamlessly upload documents.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            handleClose(); // Close the dialog
                            navigate('/integrations'); // Navigate to the integrations page
                          }}
                        >
                          Go to Integrations
                        </Button>
                      </Box>
                    ) : (
                      <>
                        {integrationsLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <List>
                            {integrations.map((integration) => (
                              <ListItem
                                key={integration.id}
                                secondaryAction={
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={!integration.isConnected}
                                    onClick={() => {
                                      setSelectedIntegration(integration);
                                      const integrationName = integration.name.toLowerCase();
                                      let initialFolderId = 'root';
                                      if (integrationName === 'dropbox') {
                                        initialFolderId = '';
                                      } else if (integrationName === 'box') {
                                        initialFolderId = '0';
                                      }
                                      setCurrentFolderId(initialFolderId);
                                      setFolderPath([{ id: initialFolderId, name: integration.name }]);
                                    }}
                                  >
                                    {integration.isConnected ? 'Open' : 'Connect'}
                                  </Button>
                                }
                              >
                                <ListItemIcon>
                                  <img
                                    src={`/logos/${integration.name.toLowerCase().replace(/\s/g, '')}.png`}
                                    alt={`${integration.name} logo`}
                                    width="24"
                                    height="24"
                                    style={{ objectFit: 'contain' }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={integration.name}
                                  secondary={
                                    integration.isConnected ? (
                                      <Typography variant="caption" color="green">
                                        Connected
                                      </Typography>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        Disconnected
                                      </Typography>
                                    )
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Processing Status */}
            {processingStatus.stage !== 'idle' && (
              <Box sx={{ mt: 2 }}>
                <Alert 
                  severity={
                    processingStatus.stage === 'error' ? 'error' : 
                    processingStatus.stage === 'completed' ? 'success' : 'info'
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {processingStatus.message}
                  </Typography>
                  {processingStatus.stage !== 'completed' && processingStatus.stage !== 'error' && (
                    <LinearProgress 
                      variant="determinate" 
                      value={processingStatus.progress} 
                      sx={{ mt: 1 }}
                    />
                  )}
                </Alert>
                
                {processingStatus.stage === 'processing' && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 1, color: 'primary.main', fontWeight: 'medium' }}>
                      {processingMessages[processingMessageIndex]}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      This may take a few moments
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Uploaded Files List */}
            {dealData.uploadedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Uploaded Files ({dealData.uploadedFiles.length})
                </Typography>
                <List dense>
                  {dealData.uploadedFiles.map((file) => (
                    <ListItem key={file.id}>
                      <ListItemIcon>
                        <DescriptionIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          setDealData(prev => ({
                            ...prev,
                            uploadedFiles: prev.uploadedFiles.filter((f: any) => f.id !== file.id),
                          }));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Fund Selection Section - Show when there are any documents (uploaded or selected existing) */}
            {(dealData.uploadedFiles.length > 0 || selectedExistingDocuments.size > 0) && processingStatus.stage === 'idle' && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AssessmentIcon color="primary" />
                  <Typography variant="h6">Deal Scoring</Typography>
                </Box>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Choose scoring criteria:</strong> Select "Auto" to score against the best matching fund based on industry and criteria, or choose a specific fund to score against.
                  </Typography>
                </Alert>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Fund Selection</InputLabel>
                  <Select
                    value={selectedFund}
                    onChange={(e) => setSelectedFund(e.target.value)}
                    label="Fund Selection"
                  >
                    <MenuItem value="auto">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AutoAwesomeIcon fontSize="small" color="primary" />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">Auto (Recommended)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Score against best matching fund based on industry and investment criteria
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    {funds.map((fund: any) => (
                      <MenuItem key={fund.id} value={fund.id}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{fund.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fund.vintage} • ${fund.total_commitments?.toLocaleString()}M • {fund.status}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {selectedFund !== 'auto' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Manual Selection:</strong> The deal will be scored specifically against the selected fund's investment thesis and criteria.
                    </Typography>
                  </Alert>
                )}

                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>What happens next:</strong>
                  </Typography>
                  <List dense sx={{ mt: 1 }}>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <AutoAwesomeIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="AI will extract financial data, company info, and key metrics"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <AssessmentIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={`Deal will be scored against ${selectedFund === 'auto' ? 'best matching fund' : 'selected fund'}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Risk flags and due diligence questions will be generated"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </Alert>
              </Box>
            )}
            
            {(dealData.uploadedFiles.length === 0 && selectedExistingDocuments.size === 0) && processingStatus.stage === 'idle' && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <DescriptionIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  No documents uploaded yet. Documents are optional but enable AI-powered analysis.
                </Typography>
              </Box>
            )}
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Create New Deal</DialogTitle>
        <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {getStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={!processingStatus.canClose}>
          {processingStatus.stage === 'completed' ? 'Close' : 'Cancel'}
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting || !processingStatus.canClose}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button onClick={handleNext} variant="contained" disabled={activeStep === 1 && !!dateError}>
            Next
          </Button>
        ) : processingStatus.stage === 'completed' ? (
          <Button 
            onClick={() => {
              resetState();
              setActiveStep(0);
            }} 
            variant="contained"
            startIcon={<AddIcon />}
          >
            Create Another Deal
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={isSubmitting || !processingStatus.canClose || !!dateError}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <BusinessIcon />}
          >
            {isSubmitting ? 'Creating...' : 'Create Deal'}
          </Button>
        )}
        {processingStatus.stage === 'completed' && createdDealId && (
          <Button
            variant="outlined"
            onClick={() => {
              handleClose();
              navigate(`/deals/${createdDealId}`);
            }}
          >
            View Deal
          </Button>
        )}
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={!!passwordPrompt} onClose={handlePasswordCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Password Required</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The PDF "{passwordPrompt?.fileName}" is password protected. Please enter the password to continue.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="PDF Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && password) {
                handlePasswordSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordCancel}>Cancel</Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={!password || isCheckingPassword}
            startIcon={isCheckingPassword ? <CircularProgress size={20} /> : null}
          >
            {isCheckingPassword ? 'Verifying...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateDealDialog;
